import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowClockwise, CheckCircle, ClipboardText, MagnifyingGlass, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const publicationLabel = (exam) => {
  if (exam.results_are_visible && exam.bulletins_are_visible) return "Resultados e boletins publicados";
  if (exam.results_are_visible) return "Resultados publicados · boletins retidos";
  return "Resultados retidos";
};

export default function CorrectionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const stage = searchParams.get("stage") === "publication" ? "publication" : "corrections";
  const [exams, setExams] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [examResponse, semesterResponse] = await Promise.all([api.get("/exams"), api.get("/semesters")]);
      setExams((examResponse.data || []).filter((item) => item.can_write));
      setSemesters(semesterResponse.data || []);
    } catch (requestError) {
      const message = fmtError(requestError);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const semesterNames = useMemo(() => new Map(semesters.map((item) => [item.id, item.name])), [semesters]);
  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    return exams.filter((exam) => {
      if (stage === "publication" && Number(exam.card_count || 0) < 1) return false;
      if (!term) return true;
      return (exam.name || "").toLocaleLowerCase("pt-BR").includes(term)
        || (semesterNames.get(exam.semester_id) || "").toLocaleLowerCase("pt-BR").includes(term);
    });
  }, [exams, search, semesterNames, stage]);

  const summaries = useMemo(() => ({
    examsWithCards: exams.filter((item) => Number(item.card_count || 0) > 0).length,
    pendingReviews: exams.reduce((total, item) => total + Number(item.pending_review_count || 0), 0),
    pendingDuplicates: exams.reduce((total, item) => total + Number(item.pending_duplicate_count || 0), 0),
    withheld: exams.filter((item) => Number(item.card_count || 0) > 0 && !item.results_are_visible).length,
  }), [exams]);

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-10">
      <div className="border-b border-border pb-7 mb-7 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
        <div>
          <div className="label-eyebrow mb-2">fluxo editorial · resultados sob controle</div>
          <h1 className="font-display text-5xl font-black tracking-tighter leading-none">Correções.</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-2xl">Revise os cartões e ajuste o que for necessário. Resultados e boletins só aparecem para os alunos depois de uma autorização explícita.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
          <ArrowClockwise size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Etapas do fluxo de correção">
        <button type="button" role="tab" aria-selected={stage === "corrections"} onClick={() => setSearchParams({ stage: "corrections" })} className={`px-5 py-3 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${stage === "corrections" ? "bg-[hsl(var(--warning))] text-black border-[hsl(var(--warning))]" : "border-border bg-card"}`}>
          <ClipboardText size={15} weight="bold" /> 1. Correções
        </button>
        <button type="button" role="tab" aria-selected={stage === "publication"} onClick={() => setSearchParams({ stage: "publication" })} className={`px-5 py-3 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${stage === "publication" ? "bg-[hsl(var(--success))] text-white border-[hsl(var(--success))]" : "border-border bg-card"}`}>
          <CheckCircle size={15} weight="bold" /> 2. Publicação dos resultados
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          ["provas com cartões", summaries.examsWithCards],
          ["revisões pendentes", summaries.pendingReviews],
          ["duplicidades", summaries.pendingDuplicates],
          ["resultados retidos", summaries.withheld],
        ].map(([label, value]) => (
          <div key={label} className="border border-border bg-card p-4"><div className="label-eyebrow">{label}</div><div className="font-display text-3xl font-black mt-2">{value}</div></div>
        ))}
      </div>

      <div className="relative max-w-xl mb-5">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar prova ou semestre…" className="rounded-none pl-10 font-mono-data" />
      </div>

      {error && <div className="border border-destructive bg-destructive/5 p-5 text-sm mb-5"><b>Não foi possível carregar as correções.</b><div className="text-muted-foreground mt-1">{error}</div></div>}
      {loading ? (
        <div className="border border-border bg-card p-16 text-center label-eyebrow">carregando fluxo de correções…</div>
      ) : !filtered.length ? (
        <div className="border border-border bg-card p-16 text-center label-eyebrow">nenhuma prova nesta etapa</div>
      ) : (
        <div className="border border-border bg-card divide-y divide-border">
          {filtered.map((exam) => {
            const reviewPending = Number(exam.pending_review_count || 0);
            const duplicatePending = Number(exam.pending_duplicate_count || 0);
            const identityPending = Number(exam.unlinked_student_cards || 0);
            const blockers = reviewPending + duplicatePending + identityPending;
            return (
              <article key={exam.id} className="p-5 flex flex-col xl:flex-row xl:items-center gap-5" data-testid={`correction-exam-${exam.id}`}>
                <div className={`w-11 h-11 shrink-0 flex items-center justify-center border ${blockers ? "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10" : "border-[hsl(var(--success))] bg-[hsl(var(--success))]/10"}`}>
                  {blockers ? <Warning size={23} weight="fill" className="text-[hsl(var(--warning))]" /> : <CheckCircle size={23} weight="fill" className="text-[hsl(var(--success))]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-data font-bold truncate">{exam.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{semesterNames.get(exam.semester_id) || "Sem semestre"} · {Number(exam.card_count || 0)} cartão(ões)</div>
                  <div className={`text-xs font-mono-data mt-2 ${exam.results_are_visible ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>{publicationLabel(exam)}</div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {!!reviewPending && <span className="border border-[hsl(var(--warning))]/50 px-2 py-1 text-[10px] font-mono-data">{reviewPending} revisão(ões)</span>}
                    {!!duplicatePending && <span className="border border-destructive/50 px-2 py-1 text-[10px] font-mono-data">{duplicatePending} duplicidade(s)</span>}
                    {!!identityPending && <span className="border border-[hsl(var(--warning))]/50 px-2 py-1 text-[10px] font-mono-data">{identityPending} aluno(s) sem vínculo</span>}
                    {!blockers && Number(exam.card_count || 0) > 0 && <span className="border border-[hsl(var(--success))]/50 px-2 py-1 text-[10px] font-mono-data text-[hsl(var(--success))]">pronta para publicar</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Link to={`/exams?open=${encodeURIComponent(exam.id)}&tab=review`} className="px-4 py-2 border border-foreground text-xs font-mono-data uppercase tracking-widest hover:bg-foreground hover:text-background">Abrir correções</Link>
                  <Link to={`/exams?open=${encodeURIComponent(exam.id)}&tab=results`} className="px-4 py-2 border border-primary text-primary text-xs font-mono-data uppercase tracking-widest hover:bg-primary hover:text-primary-foreground">Conferir resultados</Link>
                  <Link to={`/exams?open=${encodeURIComponent(exam.id)}&tab=publication`} className="px-4 py-2 border border-[hsl(var(--success))] text-[hsl(var(--success))] text-xs font-mono-data uppercase tracking-widest hover:bg-[hsl(var(--success))] hover:text-white">Publicação</Link>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
